import React, { FC, ReactNode, useRef, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { getCurrentStore } from '../../selectors/metaData';
import {
  gtmListingProductImpressions,
  luxeGtmProductImpressions,
  getStoreName
} from '../../../modules/ga/GTMdatalayer';
import { getDiscountValue } from '../../../utils/price';
import luxeScreen from '../../constants/luxeScreenType';
import { readCookie } from '../../../components/common/Cookie';
import { getGiftWithProductEnable } from '../../selectors/featureFlagsSelector';

type Item = {
  id?: string | number;
  source: string;
  description: string;
};

interface VerticalScrollableProps {
  items: Array<Partial<Item>>;
  renderItem: (item: any, idx: number) => ReactNode | FC;
  currentStore?: string;
  containerClassNames?: string;
  pageType?: string;
  productWidgetCard?: boolean;
  pageList?: string;
  luxe?: boolean | undefined;
  isPDPScroll?: string;
  similarProductsVariant?: boolean;
  widgetTitle?: string;
  enableGiftWithProduct?: boolean;
  parentProduct?: any;
}

const VerticalScrollable: FC<VerticalScrollableProps> = props => {
  const {
    items,
    renderItem,
    containerClassNames,
    productWidgetCard,
    pageList,
    luxe,
    currentStore,
    widgetTitle = 'productImpressions',
    parentProduct
  } = props;

  const [viewedProducts, setViewedProducts] = useState<Item[]>([]);
  const [viewedOldProducts, setViewedOldProducts] = useState<Item[]>([]);
  const valueRef = useRef<Item[]>([]);

  useEffect(() => {
    setViewedProducts([]);
    setViewedOldProducts([]);
  }, [items]);

  const onScroll = () => {
    items.forEach((product, index) => {
      let productCardId = widgetTitle ? `product_${widgetTitle}_${index}` : `product_${index}`;
      let productId = document.getElementById(productCardId);

      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            const productIds = JSON.parse(localStorage.getItem(`${widgetTitle}_ids`)) ?? [];
            let productExist = productIds.find(item => item === product.code);
            if (productExist === undefined) {
              const productImpressions = JSON.parse(localStorage.getItem(widgetTitle)) ?? [];
              const newProductImpressions = [...productImpressions, product];
              const newProductIds = [...productIds, product.code];
              localStorage.setItem(widgetTitle, JSON.stringify(newProductImpressions));
              localStorage.setItem(`${widgetTitle}_ids`, JSON.stringify(newProductIds));

              if (newProductImpressions.length === 10) {
                productImpressionsEventPush(newProductImpressions);
                localStorage.removeItem(widgetTitle);
              }
            }
          }
        });
      });
      if (productId) {
        observer.observe(productId);
      }
    });
  };

  const productImpressionsEventPush = (product: Array): void => {
    let impressions = [];
    const {
      pdp: { screenName = '', screenType = '' },
      landing,
      pdpList: { similarList = '' }
    } = luxeScreen;
    const { enableGiftWithProduct = false } = props;
    product.map((data, index) => {
      const {
        description,
        brand,
        code = '',
        priceValue,
        productColor,
        productCategory,
        brickName,
        productSize,
        discount = '',
        isExclusive,
        productTag,
        productTagEngineValue,
        productPriceLabel,
        segmentId = '',
        experimentId = '',
        offerPrice: { giftProductCount = 0 } = {},
        catalogName = ''
      } = data;
      let productId = code;
      let tag = isExclusive ? 'Exclusive' : productTag;
      const dimension60Data =
        Number(segmentId) || Number(experimentId) ? `${segmentId}|${experimentId}` : '';

      let gwpDimension = luxe ? '' : productPriceLabel || '';
      if (enableGiftWithProduct && giftProductCount > 0) {
        gwpDimension = gwpDimension
          ? `${gwpDimension}, GetFreebies - ${giftProductCount}`
          : `GetFreebies - ${giftProductCount}`;
      }

      const pageList = (() => {
        if (widgetTitle.toLowerCase() === 'similar') {
          return similarList;
        }

        return '';
      })();

      impressions.push({
        name: description,
        id: productId,
        price: priceValue,
        brand,
        category: productCategory,
        variant: productColor ? productColor.split('_')[1] : '',
        list: pageList, // example only,
        position: index,
        dimension6: description, // pass value for product_name
        dimension7: productId, // pass value for product_id
        dimension8: brand, // pass value for product_brand
        dimension9: priceValue, // pass value for product_price
        dimension10: productColor ? productColor.split('_')[1] : '', // pass value for product_color
        dimension11: productSize, // pass value for product_size
        dimension12: discount, // pass value for product_trade_discount
        dimension13: productCategory || '', // pass value for product_category
        dimension14: brickName, // pass value for product_brick
        dimension15: tag, // pass value for product_tag
        dimension16: currentStore, // pass value for Store_type
        dimension23: getStoreName(),
        dimension60: dimension60Data,
        productTagEngineValue: productTagEngineValue || '',
        productPriceLabel: productPriceLabel || '',
        product_trade_discount: discount,
        gwpDimension,
        catalogName: catalogName ?? "",
      });
    });
    if (luxe) {
      let luxeImpressions = [...impressions];
      luxeImpressions.map(res => {
        res.dimension89 = res.productTagEngineValue;
        res.dimension90 = res.productPriceLabel;
        res.dimension91 = '';
        res.dimension82 = res.gwpDimension;
        delete res.gwpDimension;
        delete res.productTagEngineValue;
        delete res.productPriceLabel;
        delete res.product_trade_discount;
        return res;
      });
      luxeGtmProductImpressions({
        ...luxeImpressions,
        // list: productWidgetCard ? landing.screenName : pageList,
        store_type: currentStore,
        page_name: productWidgetCard ? landing.screenName : screenName,
        page_type: productWidgetCard ? landing.screenType : screenType,
        contains_luxe: !!luxe,
        impressions,
        parentProduct: parentProduct
      });
    } else {
      let ajioImpressions = [...impressions];
      ajioImpressions.map(res => {
        res.dimension165 = res.productTagEngineValue;
        res.dimension166 = res.gwpDimension;
        res.dimension167 = '';
        res.dimension170 = getDiscountValue(res.product_trade_discount);
        delete res.gwpDimension;
        delete res.productTagEngineValue;
        delete res.productPriceLabel;
        delete res.product_trade_discount;
        return res;
      });
      gtmListingProductImpressions({
        impressions: ajioImpressions,
        // list: productWidgetCard ? landing.screenName : pageList,
        store_type: currentStore,
        page_name: productWidgetCard ? landing.screenName : screenName,
        page_type: productWidgetCard ? landing.screenType : screenType,
        contains_luxe: !!luxe,        
      },'','','','','','',parentProduct,'VerticalScrollable','Vertical','');
    }
  };

  const sendRemainingImpressions = () => {
    const productImpressions = JSON.parse(localStorage.getItem(widgetTitle)) ?? [];
    if (productImpressions.length > 0) {
      productImpressionsEventPush(productImpressions);
      localStorage.removeItem(widgetTitle);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    window.addEventListener('beforeunload', sendRemainingImpressions);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('beforeunload', sendRemainingImpressions);
    };
  }, []);

  return (
    <div className={containerClassNames} onScroll={onScroll} ref={valueRef}>
      {items.map((data, index) => renderItem(data, index))}
    </div>
  );
};

const mapStateToProps = state => {
  return {
    currentStore: getCurrentStore(state),
    enableGiftWithProduct: getGiftWithProductEnable(state?.flags)
  };
};
export default connect(mapStateToProps, null)(VerticalScrollable);